import { AutoAwesome, Storefront } from '@mui/icons-material';
import { Box, Chip, Stack, Typography } from '@mui/material';
import { useLayoutEffect, useRef, useState } from 'react';
import ChatInput from '../../components/chat/ChatInput';
import ChatMessage, { TypingMessage } from '../../components/chat/ChatMessage';
import ProductDetail from '../../components/chat/ProductDetail';
import ChatService from '../../services/chatService';

const suggestions = ['Busco vestidos', 'Quiero ver jeans', 'Busco ropa de dama', 'Quiero ver novedades'];

const createSessionId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `chat-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const ChatPage = () => {
  const [sessionId] = useState(createSessionId);
  const [messages, setMessages] = useState([]);
  const [sending, setSending] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const scrollRef = useRef(null);
  const bottomRef = useRef(null);
  const shouldStickToBottomRef = useRef(true);

  useLayoutEffect(() => {
    if (!shouldStickToBottomRef.current) return;
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, sending]);

  const handleScroll = (event) => {
    const { scrollHeight, scrollTop, clientHeight } = event.currentTarget;
    shouldStickToBottomRef.current = scrollHeight - scrollTop - clientHeight < 80;
  };

  const sendMessage = async (content) => {
    if (sending) return;
    shouldStickToBottomRef.current = true;
    setMessages((current) => [...current, { id: `user-${Date.now()}`, role: 'user', content }]);
    setSending(true);
    try {
      const response = await ChatService.sendMessage(sessionId, content);
      setMessages((current) => [...current, { id: `assistant-${Date.now()}`, role: 'assistant', content: response.content, products: response.products }]);
    } catch (error) {
      console.error('Error sending chat message:', error);
      setMessages((current) => [...current, { id: `assistant-error-${Date.now()}`, role: 'assistant', content: 'No pude consultar los productos en este momento. Probá nuevamente en unos segundos.' }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <Box sx={{ height: '100dvh', overflow: 'hidden', bgcolor: '#f8f3f0', display: 'flex', justifyContent: 'center', color: '#3e2d2d' }}>
      <Box sx={{ width: '100%', maxWidth: 920, height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', bgcolor: '#fdf9f7', boxShadow: { md: '0 0 30px rgba(77, 43, 44, 0.12)' } }}>
        <Box component="header" sx={{ position: 'sticky', top: 0, zIndex: 1, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 1.25, px: 2, py: 1.5, bgcolor: '#a52a4b', color: '#fff', pt: 'max(12px, env(safe-area-inset-top))' }}>
          <Box sx={{ width: 38, height: 38, display: 'grid', placeItems: 'center', borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.18)' }}><Storefront fontSize="small" /></Box>
          <Box><Typography sx={{ fontWeight: 800, lineHeight: 1.2 }}>Jazmin</Typography><Typography variant="caption" sx={{ opacity: 0.88 }}>Asistente de ventas</Typography></Box>
          <AutoAwesome sx={{ ml: 'auto', fontSize: 19, opacity: 0.9 }} />
        </Box>
        <Box ref={scrollRef} onScroll={handleScroll} sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: { xs: 1.5, sm: 3 }, py: 2, overscrollBehavior: 'contain' }}>
          {messages.length === 0 ? (
            <Stack alignItems="center" textAlign="center" sx={{ maxWidth: 500, mx: 'auto', pt: { xs: 6, sm: 10 } }}>
              <Box sx={{ width: 58, height: 58, display: 'grid', placeItems: 'center', borderRadius: '50%', bgcolor: '#fbe9ee', color: '#a52a4b' }}><AutoAwesome /></Box>
              <Typography variant="h5" sx={{ mt: 2, fontWeight: 800 }}>Hola 👋 ¿Qué estás buscando?</Typography>
              <Typography sx={{ mt: 0.75 }}>Soy el asistente de Jazmin.</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Contame qué estás buscando y te ayudo a encontrarlo.</Typography>
              <Stack direction="row" flexWrap="wrap" justifyContent="center" gap={1} sx={{ mt: 3 }}>
                {suggestions.map((suggestion) => <Chip key={suggestion} label={suggestion} onClick={() => sendMessage(suggestion)} disabled={sending} clickable sx={{ bgcolor: '#fff', border: '1px solid #eadfdb', '&:hover': { bgcolor: '#fbe9ee' } }} />)}
              </Stack>
            </Stack>
          ) : (
            <Stack spacing={1.5} sx={{ maxWidth: 760, mx: 'auto' }}>
              {messages.map((message) => <ChatMessage key={message.id} message={message} onProductClick={setSelectedProduct} />)}
              {sending && <TypingMessage />}
              <Box ref={bottomRef} aria-hidden="true" />
            </Stack>
          )}
        </Box>
        <Box sx={{ flexShrink: 0 }}><ChatInput disabled={sending} onSend={sendMessage} /></Box>
      </Box>
      <ProductDetail product={selectedProduct} open={Boolean(selectedProduct)} onClose={() => setSelectedProduct(null)} onChoose={setSelectedProduct} />
    </Box>
  );
};

export default ChatPage;